import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { ENV_CONFIG } from '@core/config/env.token';
import { createRequestId, withHttpRetry, withRequestId } from '@core/http/http-context';
import type { CheckInPayload, CheckOutPayload, PayloadLocation } from '@core/remito.model';

export interface DeliveryNotesCheckoutItemPayload {
  qrCode: string;
  scanDate: string;
  location: PayloadLocation;
  observation: string;
  imageKeys: string[];
  pageNumber?: number;
}

export interface DeliveryNotesCheckoutPayload {
  sessionId: string;
  checkIn: CheckInPayload;
  checkout: CheckOutPayload;
  qrCode: string;
  scanDate: string;
  location: PayloadLocation;
  observation: string;
  imageKeys: string[];
  deliveryNotes?: DeliveryNotesCheckoutItemPayload[];
}

export interface DeliveryNotesCheckoutResponse {
  success?: boolean;
  message?: string;
  data?: unknown;
}

export interface DeliveryNotesCheckoutRequest {
  payload: DeliveryNotesCheckoutPayload;
  images: {
    file: File | Blob;
    imageName: string;
  }[];
  imageKeys: string[];
  requestId?: string;
}

interface CheckoutConsolePayload {
  url: string;
  requestId: string;
  payload: DeliveryNotesCheckoutPayload;
  imageKeys: string[];
  images: {
    file: File | Blob;
    imageName: string;
    imageKey: string;
    size: number;
    type: string;
  }[];
}

@Injectable({ providedIn: 'root' })
export class DeliveryNotesApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ENV_CONFIG);
  private readonly baseUrl = this.config.apiUrl.replace(/\/+$/, '');

  async verificarConexion(): Promise<boolean> {
    const requestId = createRequestId('health-check');
    try {
      const response = await firstValueFrom(
        this.http.get<{ status: string }>(`${this.baseUrl}/health`, { context: withRequestId(requestId, withHttpRetry()) })
          .pipe(timeout(5000))
      );
      return response.status === 'ok';
    } catch {
      return false;
    }
  }

  async checkout(request: DeliveryNotesCheckoutRequest): Promise<DeliveryNotesCheckoutResponse> {
    this.assertAlignedImages(request.images, request.imageKeys);
    const formData = new FormData();
    formData.append('payload', JSON.stringify(request.payload));
    if (request.images.length === 1) {
      const [image] = request.images;
      formData.append('image', image.file, image.imageName);
    }

    request.images.forEach((image, index) => {
      formData.append('images', image.file, image.imageName);
      formData.append(`imageKeys[${index}]`, request.imageKeys[index]);
    });

    const requestId = request.requestId ?? createRequestId('checkout');
    const context = withRequestId(requestId, new HttpContext());
    const headers = new HttpHeaders({ accept: 'application/json' });
    const url = `${this.baseUrl}/DeliveryNotes/checkout`;

    console.info('[DeliveryNotesApiService.checkout] Payload enviado a API', this.buildConsolePayload(request, url, requestId));

    try {
      return await firstValueFrom(
        this.http.post<DeliveryNotesCheckoutResponse>(url, formData, { context, headers })
          .pipe(timeout(this.config.timeout))
      );
    } catch (error) {
      console.error('[DeliveryNotesApiService.checkout] Error devuelto por API', this.buildConsoleError(error, url, requestId));
      throw error;
    }
  }

  private buildConsolePayload(
    request: DeliveryNotesCheckoutRequest,
    url: string,
    requestId: string
  ): CheckoutConsolePayload {
    return {
      url,
      requestId,
      payload: request.payload,
      imageKeys: request.imageKeys,
      images: request.images.map((image, index) => ({
        file: image.file,
        imageName: image.imageName,
        imageKey: request.imageKeys[index],
        size: image.file.size,
        type: image.file.type
      }))
    };
  }

  private buildConsoleError(error: unknown, url: string, requestId: string): unknown {
    if (error instanceof HttpErrorResponse) {
      return {
        url,
        requestId,
        status: error.status,
        statusText: error.statusText,
        message: error.message,
        error: error.error
      };
    }

    if (error instanceof Error) {
      return {
        url,
        requestId,
        name: error.name,
        message: error.message
      };
    }

    return {
      url,
      requestId,
      error
    };
  }

  private assertAlignedImages(
    images: DeliveryNotesCheckoutRequest['images'],
    imageKeys: string[]
  ): void {
    if (images.length === 0 || imageKeys.length === 0) {
      throw new Error('No hay imágenes para enviar');
    }

    if (images.length !== imageKeys.length) {
      throw new Error('La cantidad de imágenes no coincide con la cantidad de nombres de archivo');
    }
  }
}
